import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { ErrorMessageResponse, ErrorType } from '@/types';
import { ErrorMap } from '@/constants/errors';


export class ErrorResponse {
  private error: AxiosError;
  constructor(error: unknown) {
    this.error = error as AxiosError;
  }

  getStatus = () => {
    return this.error.status;
  };

  getType = () => {
    return this.getStatus()
      ? ErrorMap[this.getStatus() as keyof typeof ErrorMap] ||
          ErrorType.UnknownError
      : undefined;
  };

  getMessage = () => {
    return this.error.message;
  };

  getResponseMessage = () => {
    return (this.error.response?.data as ErrorMessageResponse)?.message;
  };

  /**
   * Der ganze Antwort-Body — fuer Endpunkte, die neben `message` ein
   * fachliches Flag mitgeben (z. B. `fall_bereits_gewechselt` bei der
   * Termin-Umverteilung). Bewusst generisch und unveraendert durchgereicht.
   */
  getResponseData = <T = Record<string, unknown>>(): T | undefined => {
    return this.error.response?.data as T | undefined;
  };

  /** Der ungeschachtelte Fehler, an dem die Marke „am Feld gezeigt" haengt. */
  rohFehler = (): unknown => this.error;
}

/**
 * Antwortdaten nur in der erwarteten Form uebernehmen (Triage f, W/V/FE-J).
 *
 * axios `settle()` resolved nach einem 401 mit Status 0 und leerem Body: es wird
 * nichts geworfen, `data` fehlt, und erst die Anzeige stuerzt ab. Leer ist also
 * ein bekannter Fall und bleibt still. Eine andere Form dagegen waere ein
 * geaenderter Vertrag: die Liste bleibt leer, aber Sentry erfaehrt davon, sonst
 * sieht der Nutzer eine leere Seite und niemand die Ursache.
 */
export const alsListe = <T>(wert: T[] | null | undefined, quelle: string): T[] => {
  if (Array.isArray(wert)) return wert;
  if (wert !== null && wert !== undefined && (wert as unknown) !== '') {
    Sentry.captureMessage(`Antwort ohne erwartete Liste: ${quelle} (${typeof wert})`, 'warning');
  }
  return [];
};

const MARKE_AM_FELD = '__amFeldGezeigt';

/**
 * Sagt dem zentralen 422-Toast (`services/network`), dass diese Stelle die
 * Meldung selbst am Feld oder im Formular zeigt. Ohne Marke bleibt der Toast:
 * eine Meldung doppelt ist besser als gar keine. Mit `feld` gilt die Marke nur
 * fuer dieses Feld, ein Fehler an einem anderen Feld kommt weiter als Toast.
 */
export const alsAmFeldGezeigt = (error: unknown, feld?: string): void => {
  const roh = error instanceof ErrorResponse ? error.rohFehler() : error;
  if (roh === null || typeof roh !== 'object') return;
  const objekt = roh as Record<string, unknown>;
  if (feld === undefined) {
    objekt[MARKE_AM_FELD] = true;
  } else if (objekt[MARKE_AM_FELD] !== true) {
    const bisher = objekt[MARKE_AM_FELD];
    objekt[MARKE_AM_FELD] = [...(Array.isArray(bisher) ? bisher : []), feld];
  }
};

export const istAmFeldGezeigt = (error: unknown): boolean => {
  const roh = error instanceof ErrorResponse ? error.rohFehler() : error;
  return roh !== null && typeof roh === 'object' && (roh as Record<string, unknown>)[MARKE_AM_FELD] === true;
};

/** Erste Meldung eines Feldes, das noch niemand zeigt; sonst null. */
export const ersteOffeneFeldmeldung = (error: unknown, felder: Record<string, unknown>): string | null => {
  if (istAmFeldGezeigt(error)) return null;
  const roh = (error instanceof ErrorResponse ? error.rohFehler() : error) as Record<string, unknown>;
  const gezeigt = Array.isArray(roh?.[MARKE_AM_FELD]) ? (roh[MARKE_AM_FELD] as string[]) : [];
  for (const [feld, liste] of Object.entries(felder)) {
    if (!gezeigt.includes(feld) && Array.isArray(liste) && typeof liste[0] === 'string' && liste[0] !== '') {
      return liste[0];
    }
  }
  return null;
};

/** Laravel haengt an `message` bei mehreren Fehlern z. B. „(and 1 more error)" an. */
const ZUSATZ_WEITERE_FEHLER = /\s*\(and \d+ more errors?\)\s*$/;
/** Laravel-Standardtexte ohne deutsche Fassung (`lang/` fuehrt nur `en`). */
const ENGLISCHER_STANDARDTEXT = /^(The|This|These|Validation)\s/;
const MELDUNG_STATT_ENGLISCH = 'Die Eingabe ist ungültig.';

export const deutscheFeldmeldung = (text: string): string => {
  const ohneZusatz = text.replace(ZUSATZ_WEITERE_FEHLER, '');
  return ENGLISCHER_STANDARDTEXT.test(ohneZusatz) ? MELDUNG_STATT_ENGLISCH : ohneZusatz;
};

/**
 * Bereinigt `message` und `errors` einer 422-Antwort an Ort und Stelle. Der
 * Interceptor ruft das auf, damit jede Lesestelle denselben Text bekommt, auch
 * die, die `response.data` direkt liest.
 */
export const bereinigeValidierungsAntwort = (daten: unknown): void => {
  if (daten === null || typeof daten !== 'object') return;
  const antwort = daten as { message?: unknown; errors?: unknown };
  if (typeof antwort.message === 'string') {
    antwort.message = deutscheFeldmeldung(antwort.message);
  }
  if (antwort.errors !== null && typeof antwort.errors === 'object') {
    const felder = antwort.errors as Record<string, unknown>;
    Object.keys(felder).forEach((feld) => {
      const liste = felder[feld];
      if (Array.isArray(liste)) {
        felder[feld] = liste.map((m) => (typeof m === 'string' ? deutscheFeldmeldung(m) : m));
      }
    });
  }
};

/**
 * Loest den Fehlertyp fuer den Store auf, OHNE zu unterstellen, dass der
 * gefangene Wert eine ErrorResponse ist.
 *
 * Hintergrund (Sentry BERTATERWETL-FRONTEND-A, 28.07.): die Stores casten im
 * catch blind `(e as ErrorResponse).getType()`. Die API-Schicht wirft zwar
 * ueberall ErrorResponse — aber im try-Block steht mehr als der API-Aufruf.
 * Faellt dort ein gewoehnlicher Fehler an (TypeError beim Weiterverarbeiten der
 * Antwort), hat er kein getType, der Cast wirft "a.getType is not a function"
 * und die EIGENTLICHE Ursache ist weg. Genau das war auf customer-details zu
 * sehen: der echte Fehler stand nie in Sentry, nur der Folgefehler.
 *
 * Fremde Fehler werden deshalb als UnknownError abgebildet und zusaetzlich an
 * Sentry gemeldet — die Ursache soll sichtbar bleiben, nicht ihr Schatten.
 */
export const toErrorType = (error: unknown): ErrorType => {
  if (error instanceof ErrorResponse) {
    return error.getType() ?? ErrorType.UnknownError;
  }

  Sentry.captureException(error);

  return ErrorType.UnknownError;
};

/**
 * Speicherfehler eines einzelnen Feldes MIT fachlicher Meldung fuer den Nutzer
 * (Triage §7 E, 02.11.2026: „Die E-Mail-Adresse ist ungültig …" vom Backend).
 * `EditableField` zeigt nur die Meldung dieser Klasse — jeder andere Fehler
 * bleibt beim generischen „Speichern fehlgeschlagen" (keine Technik-Texte).
 */
export class FeldSpeicherFehler extends Error {}

/** Erste Validierungsmeldung des Backends zu `field` (422 `errors[field][0]`), sonst null. */
export const feldMeldungAus = (error: unknown, field: string): string | null => {
  if (!(error instanceof ErrorResponse) || error.getStatus() !== 422) return null;
  const daten = error.getResponseData<{ errors?: Record<string, unknown> }>();
  const liste = daten?.errors?.[field];
  if (!Array.isArray(liste) || typeof liste[0] !== 'string' || liste[0] === '') return null;
  alsAmFeldGezeigt(error, field);
  return liste[0];
};

/** Wirft fuer den Feld-Speicherpfad: mit Backend-Meldung als FeldSpeicherFehler, sonst generisch. */
export const feldSpeicherFehler = (error: unknown, field: string): Error => {
  const meldung = feldMeldungAus(error, field);
  return meldung ? new FeldSpeicherFehler(meldung) : new Error('Speichern fehlgeschlagen');
};

/**
 * Erste Validierungsmeldung eines 422 — gleich welches Feld (`errors[*][0]`),
 * sonst `message` des 422. Bei jedem anderen Status oder fremdem Fehler: null.
 *
 * Fuer Anzeigen, die ein ganzes Formular abschicken und nicht wissen, welches
 * Feld der Server beanstandet (Pool-Vorschau Schritt 7, Fall 158996 am
 * 03.11.2026: „Pool-Klassifikation fehlgeschlagen" verdeckte die eigentliche
 * Ursache, eine negative Partner-Einkunft).
 */
export const ersteValidierungsMeldung = (error: unknown): string | null => {
  if (!(error instanceof ErrorResponse) || error.getStatus() !== 422) return null;
  const daten = error.getResponseData<{ message?: unknown; errors?: Record<string, unknown> }>();
  for (const liste of Object.values(daten?.errors ?? {})) {
    if (Array.isArray(liste) && typeof liste[0] === 'string' && liste[0] !== '') {
      alsAmFeldGezeigt(error);
      return liste[0];
    }
  }
  if (typeof daten?.message !== 'string' || daten.message === '') return null;
  alsAmFeldGezeigt(error);
  return daten.message;
};
